import { Button } from "../components/ui/button";
import { EyeIcon, X } from "lucide-react";
import { InputNumber, message, Modal, Radio } from "antd";
import { useEffect, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import axiosConfig, { handleMutation } from "../util/axiosConfig";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { API_ENDPOINTS, fetcher } from "../util/api";
import Switch from "./Switch";
import TextArea from "antd/es/input/TextArea";

interface CommissionRateResponse {
  name?: string;
  commissionType?: "FLAT" | "PERCENTAGE" | string;
  commissionValue?: string | number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

function ManageReferralAgent({
  selectedUser,
  handleClosePanel,
  onSubmit,
}: {
  selectedUser?: ReferralAgent;
  handleClosePanel?: () => void;
  onSubmit?: () => void;
}) {
  const [commissionType, setCommissionType] = useState<"FLAT" | "PERCENTAGE">("FLAT");
  const [commissionValue, setCommissionValue] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuspensionModalOpen, setIsSuspensionModalOpen] = useState(false);
  const [switchValue, setSwitchValue] = useState<boolean>(selectedUser?.isActive ?? false);

  const { data: commissionData, isLoading } = useSWR<CommissionRateResponse>(
    selectedUser ? API_ENDPOINTS.REFERRAL.COMMISSION_RATE : null,
    fetcher
  );

  const { trigger: setCommissionRate, isMutating: isUpdatingCommission } = useSWRMutation(
    API_ENDPOINTS.REFERRAL.COMMISSION_RATE,
    handleMutation
  );

  const { trigger: toggleReferralStatus, isMutating: updatingStatus } = useSWRMutation(
    selectedUser ? `/referral/referral-agents/${selectedUser.id}/status` : "",
    handleMutation
  );

  useEffect(() => {
    if (!commissionData) return;

    const type = commissionData.commissionType === "PERCENTAGE" ? "PERCENTAGE" : "FLAT";
    setCommissionType(type);
    setCommissionValue(String(commissionData.commissionValue ?? ""));
  }, [commissionData]);

  useEffect(() => {
    setSwitchValue(selectedUser?.isActive ?? false);
  }, [selectedUser?.isActive]);

  const saveCommissionRate = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);

    try {
      await setCommissionRate({
        agentId: selectedUser.id,
        name: commissionData?.name || `Referral Agent Commission - ${selectedUser.fullName}`,
        commissionType,
        commissionValue,
        effectiveFrom: new Date().toISOString(),
        effectiveTo: "",
      });

      message.success("Commission rate updated successfully");
      setIsModalOpen(false);
      onSubmit?.();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Unable to update commission rate");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStatus = async (nextValue?: boolean) => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await toggleReferralStatus({
        status: switchValue ? "active" : "inactive",
        reason
      });

      message.success("Referral agent status updated");
      setIsSuspensionModalOpen(false)
      onSubmit?.();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update referral agent status");
      setSwitchValue(!switchValue);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 transition-all duration-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-blue-600">Manage Referral Agent</h2>
        <Button variant="ghost" size="icon" onClick={handleClosePanel} className="hover:bg-gray-100">
          <X className="h-5 w-5 text-red-500" />
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="bg-blue-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-bold">
            {selectedUser?.fullName?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <h3 className="text-xl font-medium text-blue-800 capitalize">{selectedUser?.fullName}</h3>
        </div>

        <div className="p-5 space-y-5">
          <div className="space-y-2">
            <div className="text-sm text-gray-500">Email</div>
            <div className="text-gray-700">{selectedUser?.email || "N/A"}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-gray-500">Phone</div>
            <div className="text-gray-700">{selectedUser?.phone || "N/A"}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-gray-500">Referral Code</div>
            <div className="text-gray-700 font-semibold">{selectedUser?.referralCode || "N/A"}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-gray-500">Referral Link</div>
            <div className="text-gray-700 break-all">{selectedUser?.referralLink || "N/A"}</div>
          </div>

          {/* <div className="space-y-3 rounded-md border border-dashed p-4 bg-slate-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase text-gray-500">Current commission</div>
                <div className="text-lg font-bold text-primary">
                  {isLoading
                    ? "Loading..."
                    : `${commissionData?.commissionValue ?? 0}${commissionData?.commissionType === "PERCENTAGE" ? "%" : ""}`}
                </div>
              </div>
              <div className="flex items-center">
                <Button className="text-white flex-1" onClick={() => setIsModalOpen(true)}>
                  Set commission rate
                </Button>
              </div>
            </div>
          </div> */}

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-slate-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Total Referrals</div>
              <div className="text-xl font-bold text-blue-600">{selectedUser?.totalReferrals ?? 0}</div>
            </div>
            <div className="rounded-md bg-slate-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Status</div>
              <div className="mt-2 flex items-center gap-2">
                <Switch
                  switchValue={switchValue}
                  setSwitchValue={(value: boolean) => {
                    setIsSuspensionModalOpen(true)
                    setSwitchValue(value);
                  }}
                />
                <span className={`text-sm font-medium ${switchValue ? "text-green-600" : "text-red-500"}`}>
                  {switchValue ? "Active" : "Inactive"}
                </span>
                {updatingStatus && <LoadingSpinner color="grey" />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-md bg-slate-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Earned</div>
              <div className="text-sm font-bold">{selectedUser?.earnedCommission ?? 0}</div>
            </div>
            <div className="rounded-md bg-slate-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Paid</div>
              <div className="text-sm font-bold">{selectedUser?.paidCommission ?? 0}</div>
            </div>
            <div className="rounded-md bg-slate-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Pending</div>
              <div className="text-sm font-bold">{selectedUser?.pendingCommission ?? 0}</div>
            </div>
          </div>

        </div>
      </div>

      <Modal
        title="Set commission rate"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        width={420}
      >
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Commission Type</label>
            <Radio.Group
              value={commissionType}
              onChange={(e) => setCommissionType(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio value="FLAT">Flat</Radio>
              <Radio value="PERCENTAGE">Percentage</Radio>
            </Radio.Group>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Commission Value</label>
            <InputNumber
              value={commissionValue ? Number(commissionValue) : undefined}
              onChange={(value) => setCommissionValue(value?.toString() || "")}
              min={0}
              className="w-full"
              addonAfter={commissionType === "PERCENTAGE" ? "%" : ""}
              placeholder={commissionType === "PERCENTAGE" ? "e.g. 5" : "e.g. 100"}
              disabled={isLoading || isSubmitting}
            />
          </div>

          <Button
            className="w-full text-white"
            onClick={saveCommissionRate}
            disabled={isLoading || isSubmitting || !commissionValue}
          >
            {isSubmitting ? "Saving..." : "Save commission rate"}
          </Button>
        </div>
      </Modal>
      <Modal
        title="Set Suspension Reason"
        open={isSuspensionModalOpen}
        onCancel={() => setIsSuspensionModalOpen(false)}
        okButtonProps={{
          loading: isSubmitting,
          title: 'Confirm',
          onClick: () => toggleStatus()
        }}
        centered
        width={420}
      >
        <TextArea
          value={reason}
          rows={3}
          placeholder="Enter reason for suspension"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  );
}

export default ManageReferralAgent;
